import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Paper,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Tab,
  Tabs,
  TextField,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green, red, grey, blue, orange } from "@material-ui/core/colors";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PersonIcon from "@material-ui/icons/Person";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import QueueIcon from "@material-ui/icons/Queue";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { translateMessageMarker } from "../../utils/messagePreview";
import { toast } from "react-toastify";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import TicketModal from "../../components/TicketModal";

const useStyles = makeStyles((theme) => ({
  "@keyframes monitorAlarmBlink": {
    "0%, 45%": {
      background: "linear-gradient(135deg, #b71c1c 0%, #d32f2f 60%, #ef5350 100%)",
      borderLeftColor: "#7f0000",
      color: "#fff",
    },
    "55%, 100%": {
      background: theme.palette.action.hover,
      borderLeftColor: "transparent",
      color: theme.palette.text.primary,
    },
  },

  "@keyframes monitorAlarmMessageBlink": {
    "0%, 45%": {
      color: "rgba(255,255,255,0.92)",
    },
    "55%, 100%": {
      color: theme.palette.text.secondary,
    },
  },

  "@keyframes monitorAlarmTimeBlink": {
    "0%, 45%": {
      color: "#fff",
    },
    "55%, 100%": {
      color: theme.palette.text.secondary,
    },
  },

  "@keyframes monitorAlarmWaitingBlink": {
    "0%, 45%": {
      color: "#fff",
    },
    "55%, 100%": {
      color: orange[700],
    },
  },

  "@keyframes monitorAlarmCriticalBlink": {
    "0%, 45%": {
      color: "#fff",
    },
    "55%, 100%": {
      color: red[700],
    },
  },

  root: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    minHeight: "calc(100vh - 64px)",
  },
  header: {
    marginBottom: theme.spacing(3),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  tabsContainer: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  alarmTab: {
    backgroundColor: red[700],
    color: "#fff",
    borderRadius: theme.shape.borderRadius,
    minHeight: 40,
    "& .MuiTab-wrapper": {
      color: "#fff",
      fontWeight: 700,
    },
  },
  filtersContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    flexWrap: "wrap",
  },
  summaryContainer: {
    marginBottom: theme.spacing(3),
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  summaryCard: {
    padding: theme.spacing(2),
    textAlign: "center",
    minWidth: 150,
  },
  summaryValue: {
    fontSize: "2rem",
    fontWeight: "bold",
  },
  summaryLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
  },
  sectionTitle: {
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
    fontWeight: 600,
  },
  attendantsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(450px, 1fr))",
    gap: theme.spacing(3),
  },
  attendantCard: {
    padding: theme.spacing(2),
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "box-shadow 0.3s ease",
    "&:hover": {
      boxShadow: theme.shadows[4],
    },
  },
  attendantHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  attendantAvatar: {
    width: 48,
    height: 48,
    backgroundColor: theme.palette.primary.main,
  },
  attendantInfo: {
    flex: 1,
  },
  attendantName: {
    fontWeight: 600,
    fontSize: "1.1rem",
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontSize: "0.75rem",
  },
  online: {
    color: green[500],
  },
  offline: {
    color: grey[500],
  },
  ticketsList: {
    flex: 1,
    maxHeight: 350,
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  ticketItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    borderLeft: "4px solid transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.selected,
    },
  },
  ticketItemAlarm: {
    background: "linear-gradient(135deg, #b71c1c 0%, #d32f2f 60%, #ef5350 100%)",
    borderLeft: "4px solid #7f0000",
    animation: "$monitorAlarmBlink 1.4s ease-in-out infinite",
    transition: "background 0.35s ease, border-left-color 0.35s ease",
    "&:hover": {
      background: "linear-gradient(135deg, #8e0000 0%, #b71c1c 60%, #d32f2f 100%)",
    },
    "& $ticketMessage": {
      animation: "$monitorAlarmMessageBlink 1.4s ease-in-out infinite",
    },
    "& $ticketTime": {
      animation: "$monitorAlarmTimeBlink 1.4s ease-in-out infinite",
    },
    "& $ticketTime .MuiTypography-root": {
      animation: "$monitorAlarmTimeBlink 1.4s ease-in-out infinite",
    },
    "& $ticketTime svg": {
      animation: "$monitorAlarmTimeBlink 1.4s ease-in-out infinite",
    },
    "& $waitingAlert": {
      animation: "$monitorAlarmWaitingBlink 1.4s ease-in-out infinite",
    },
    "& $criticalAlert": {
      animation: "$monitorAlarmCriticalBlink 1.4s ease-in-out infinite",
    },
  },
  alarmSectionCard: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderLeft: `6px solid ${red[700]}`,
    background: "linear-gradient(135deg, rgba(183, 28, 28, 0.08) 0%, rgba(244, 67, 54, 0.02) 100%)",
  },
  ticketAvatar: {
    width: 40,
    height: 40,
  },
  ticketInfo: {
    flex: 1,
    minWidth: 0,
  },
  ticketContact: {
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  ticketMessage: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  ticketTime: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    textAlign: "right",
    minWidth: 45,
  },
  waitingAlert: {
    color: orange[700],
    fontWeight: 600,
  },
  criticalAlert: {
    color: red[700],
    fontWeight: 600,
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  totalTickets: {
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const formatTime = (seconds) => {
  if (!seconds || seconds < 0) return "--:--";
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m ${secs}s`;
};

const formatTimeShort = (dateStr) => {
  if (!dateStr) return "--:--";
  const date = new Date(dateStr);
  return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

const getWaitingTimeClass = (seconds, classes, warningThresholdSeconds = 600) => {
  if (!seconds) return "";
  if (seconds > 1800) return classes.criticalAlert; // > 30 minutos
  if (seconds > warningThresholdSeconds) return classes.waitingAlert;
  return "";
};

const formatMinutes = (minutes) => {
  if (minutes === null || minutes === undefined || minutes === "") return "--";
  if (!Number.isFinite(Number(minutes))) return "--";

  const totalMinutes = Math.max(0, Math.floor(Number(minutes)));
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;

  return `${String(hours).padStart(2, "0")}h ${String(mins).padStart(2, "0")}m`;
};

const AttendanceMonitor = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [medianMetrics, setMedianMetrics] = useState({
    waitTime: 0,
    supportTime: 0,
  });
  const [selectedTab, setSelectedTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState("open");
  const [searchFilter, setSearchFilter] = useState("");
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketUuid, setTicketUuid] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [alarmEnabled, setAlarmEnabled] = useState(true);
  const [snapshotTs, setSnapshotTs] = useState(Date.now());
  const [nowTs, setNowTs] = useState(Date.now());
  const fetchInFlightRef = useRef(false);
  const lastSocketRefreshRef = useRef(0);

  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);

  const isPendingMode = statusFilter === "pending";
  const isAllMode = statusFilter === "all";
  const showQueueSection = isPendingMode || isAllMode;
  const alarmSummary = data?.alarmSummary;
  const openAlarmCount = alarmSummary?.openTicketsInAlarm || 0;
  const pendingAlarmCount = alarmSummary?.pendingTicketsInAlarm || 0;
  const configuredAlarmMinutes = Number(alarmSummary?.alarmMinutes);
  const warningThresholdSeconds =
    Number.isFinite(configuredAlarmMinutes) && configuredAlarmMinutes > 0
      ? configuredAlarmMinutes * 60
      : 10 * 60;

  const handleStatusChange = (event) => {
    const rawValue = event?.target?.value;
    const nextStatus = typeof rawValue === "string" ? rawValue : String(rawValue || "open");

    if (["open", "pending", "all"].includes(nextStatus)) {
      setStatusFilter(nextStatus);
      return;
    }

    setStatusFilter("open");
  };

  const fetchData = useCallback(async () => {
    if (fetchInFlightRef.current) {
      return;
    }

    fetchInFlightRef.current = true;

    try {
      setLoading(true);
      const now = new Date();
      const dateTo = now.toISOString().split("T")[0];
      const dateFrom = new Date(now.getFullYear(), now.getMonth(), 1)
        .toISOString()
        .split("T")[0];

      const [attendanceResult, dashboardResult] = await Promise.allSettled([
        api.get("/attendance-monitor", {
          params: { status: statusFilter },
        }),
        api.get("/dashboard", {
          params: {
            date_from: dateFrom,
            date_to: dateTo,
          },
        }),
      ]);

      if (attendanceResult.status !== "fulfilled") {
        throw attendanceResult.reason;
      }

      const response = attendanceResult.value?.data;
      setData(response);
      // atualizar contador visual de espera por segundo entre refreshes de API/socket.
      const snapshotTime = Date.now();
      setSnapshotTs(snapshotTime);
      setNowTs(snapshotTime);
      setAlarmEnabled(Boolean(response?.alarmSummary?.enabled));

      if (dashboardResult.status === "fulfilled") {
        const dashboardData = dashboardResult.value?.data;
        setMedianMetrics({
          waitTime: dashboardData?.counters?.avgWaitTime ?? null,
          supportTime: dashboardData?.counters?.avgSupportTime ?? null,
        });
      } else {
        setMedianMetrics({
          waitTime: null,
          supportTime: null,
        });
      }
    } catch (err) {
      console.error("Erro ao carregar dados:", err);
      toast.error(i18n.t("attendanceMonitor.toasts.loadError"));
    } finally {
      fetchInFlightRef.current = false;
      setLoading(false);
    }
  }, [statusFilter]);

  const handleToggleAlarmEnabled = async () => {
    const nextEnabled = !alarmEnabled;

    try {
      await api.put("/settings/attendanceAlarmEnabled", {
        key: "attendanceAlarmEnabled",
        value: nextEnabled ? "enabled" : "disabled",
      });

      setAlarmEnabled(nextEnabled);
      fetchData();
      toast.success(
        nextEnabled
          ? i18n.t("attendanceMonitor.toasts.alarmsEnabled")
          : i18n.t("attendanceMonitor.toasts.alarmsDisabled")
      );
    } catch (err) {
      console.error("Erro ao atualizar attendanceAlarmEnabled:", err);
      toast.error(i18n.t("attendanceMonitor.toasts.alarmsUpdateError"));
    }
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNowTs(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isPendingMode && selectedTab !== 0) {
      setSelectedTab(0);
    }
  }, [isPendingMode, selectedTab]);

  // Auto refresh a cada 30 segundos
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  // Socket para atualizações em tempo real
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    const socketEvent = `company-${companyId}-ticket`;

    const handleTicketUpdate = (eventData) => {
      if (!eventData || !["update", "delete", "create"].includes(eventData.action)) {
        return;
      }

      const now = Date.now();
      const minimumRefreshIntervalMs = 3000;

      // Evita rajadas de updates do socket gerarem várias requests seguidas.
      if (now - lastSocketRefreshRef.current < minimumRefreshIntervalMs) {
        return;
      }

      lastSocketRefreshRef.current = now;
      fetchData();
    };

    socket.on(socketEvent, handleTicketUpdate);

    return () => {
      socket.off(socketEvent, handleTicketUpdate);
    };
  }, [socketManager, fetchData]);

  const handleOpenMessages = async (ticket) => {
    try {
      // Buscar o ticket completo para obter o UUID
      const { data: ticketData } = await api.get(`/tickets/${ticket.id}`);
      setTicketUuid(ticketData.uuid);
      setTicketModalOpen(true);
    } catch (err) {
      console.error("Erro ao carregar ticket:", err);
      toast.error(i18n.t("attendanceMonitor.toasts.openTicketError"));
    }
  };

  const handleCloseTicketModal = () => {
    setTicketModalOpen(false);
    setTicketUuid(null);
  };

  const filteredAttendants = data?.attendants?.filter((attendant) => {
    if (selectedTab === 1 && !attendant.online) return false;
    if (selectedTab === 2 && attendant.online) return false;

    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      if (!attendant.name.toLowerCase().includes(search)) {
        return false;
      }
    }

    return true;
  }) || [];

  const filteredQueues = data?.queues?.filter((queue) => {
    if (searchFilter) {
      const search = searchFilter.toLowerCase();
      if (!queue.name.toLowerCase().includes(search)) {
        return false;
      }
    }

    return true;
  }) || [];

  const showEmptyState = isPendingMode
    ? filteredQueues.length === 0
    : isAllMode
    ? filteredQueues.length === 0 && filteredAttendants.length === 0
    : filteredAttendants.length === 0;

  const elapsedSinceSnapshotSeconds = Math.max(
    0,
    Math.floor((nowTs - snapshotTs) / 1000)
  );

  const getDisplayWaitTime = (ticket) => {
    const baseWaitTime = Number(ticket?.timeSinceLastClientMessage || 0);

    if (baseWaitTime <= 0) {
      return 0;
    }

    return baseWaitTime + elapsedSinceSnapshotSeconds;
  };

  return (
    <div className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography variant="h5" className={classes.title}>
            {i18n.t("attendanceMonitor.title")}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {i18n.t("attendanceMonitor.subtitle")}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <Chip
            label={
              alarmEnabled
                ? i18n.t("attendanceMonitor.alarmsOn", { minutes: alarmSummary?.alarmMinutes ?? 10 })
                : i18n.t("attendanceMonitor.alarmsOff")
            }
            color={alarmEnabled ? "secondary" : "default"}
            onClick={handleToggleAlarmEnabled}
            size="small"
          />
          <Chip
            label={autoRefresh ? i18n.t("attendanceMonitor.autoRefreshOn") : i18n.t("attendanceMonitor.autoRefreshOff")}
            color={autoRefresh ? "primary" : "default"}
            onClick={() => setAutoRefresh(!autoRefresh)}
            size="small"
          />
          <IconButton onClick={fetchData} disabled={loading}>
            <RefreshIcon className={loading ? "rotating" : ""} />
          </IconButton>
        </Box>
      </Box>

      {/* Resumo */}
      {data?.summary && (
        <Box className={classes.summaryContainer}>
          {isPendingMode ? (
            <>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: blue[600] }}>
                  {data.summary.totalAttendants}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.queues")}</Typography>
              </Paper>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: red[600] }}>
                  {data.summary.totalPendingTickets}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.waiting")}</Typography>
              </Paper>
            </>
          ) : (
            <>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: blue[600] }}>
                  {data.summary.totalAttendants}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.attendants")}</Typography>
              </Paper>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: green[600] }}>
                  {data.summary.totalOnline}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.online")}</Typography>
              </Paper>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: grey[600] }}>
                  {data.summary.totalOffline}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.offline")}</Typography>
              </Paper>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: orange[600] }}>
                  {data.summary.totalOpenTickets}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.inService")}</Typography>
              </Paper>
              <Paper className={classes.summaryCard}>
                <Typography className={classes.summaryValue} style={{ color: red[600] }}>
                  {data.summary.totalPendingTickets}
                </Typography>
                <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.waiting")}</Typography>
              </Paper>
            </>
          )}
          <Paper className={classes.summaryCard}>
            <Typography className={classes.summaryValue} style={{ color: orange[700], fontSize: "1.3rem" }}>
              {formatMinutes(medianMetrics.waitTime)}
            </Typography>
            <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.medianWait")}</Typography>
          </Paper>
          <Paper className={classes.summaryCard}>
            <Typography className={classes.summaryValue} style={{ color: blue[700], fontSize: "1.3rem" }}>
              {formatMinutes(medianMetrics.supportTime)}
            </Typography>
            <Typography className={classes.summaryLabel}>{i18n.t("attendanceMonitor.summary.medianService")}</Typography>
          </Paper>
        </Box>
      )}

      {/* Filtros */}
      <Box className={classes.filtersContainer}>
        <Paper className={classes.tabsContainer}>
          <Tabs
            value={statusFilter}
            onChange={(event, nextValue) => handleStatusChange({ target: { value: nextValue } })}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab
              value="open"
              label={`${i18n.t("attendanceMonitor.tabs.inService")}${openAlarmCount > 0 ? ` (${openAlarmCount})` : ""}`}
              className={alarmEnabled && openAlarmCount > 0 && statusFilter !== "open" ? classes.alarmTab : ""}
            />
            <Tab
              value="pending"
              label={`${i18n.t("attendanceMonitor.tabs.waiting")}${pendingAlarmCount > 0 ? ` (${pendingAlarmCount})` : ""}`}
              className={alarmEnabled && pendingAlarmCount > 0 && statusFilter !== "pending" ? classes.alarmTab : ""}
            />
            <Tab value="all" label={i18n.t("attendanceMonitor.tabs.all")} />
          </Tabs>
        </Paper>

        {!isPendingMode && (
          <Paper className={classes.tabsContainer}>
            <Tabs
              value={selectedTab}
              onChange={(e, v) => setSelectedTab(v)}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label={i18n.t("attendanceMonitor.tabs.all")} />
              <Tab label={i18n.t("attendanceMonitor.tabs.online")} />
              <Tab label={i18n.t("attendanceMonitor.tabs.offline")} />
            </Tabs>
          </Paper>
        )}
        
        <TextField
          variant="outlined"
          size="small"
          label={
            isAllMode
              ? i18n.t("attendanceMonitor.search.attendantOrQueue")
              : isPendingMode
              ? i18n.t("attendanceMonitor.search.queue")
              : i18n.t("attendanceMonitor.search.attendant")
          }
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          style={{ minWidth: 200 }}
        />
      </Box>

      {/* Loading */}
      {loading && !data && (
        <Box display="flex" justifyContent="center" padding={4}>
          <CircularProgress />
        </Box>
      )}

      {alarmEnabled && (data?.alarmTickets?.length || 0) > 0 && (
        <Paper className={classes.alarmSectionCard}>
          <Typography variant="h6" className={classes.sectionTitle} style={{ marginTop: 0 }}>
            {i18n.t("attendanceMonitor.alarmTickets", { total: data.alarmTickets.length })}
          </Typography>
          <Box className={classes.ticketsList}>
            {data.alarmTickets.map((ticket) => (
              <Box
                key={`alarm-ticket-${ticket.id}`}
                className={`${classes.ticketItem} ${classes.ticketItemAlarm}`}
                onClick={() => handleOpenMessages(ticket)}
              >
                <Avatar className={classes.ticketAvatar} src={ticket.contactProfilePicUrl}>
                  {ticket.contactName?.charAt(0)?.toUpperCase() || "?"}
                </Avatar>
                <Box className={classes.ticketInfo}>
                  <Typography className={classes.ticketContact}>{ticket.contactName}</Typography>
                  <Typography className={classes.ticketMessage} translate="no">
                    {translateMessageMarker(ticket.lastMessage) || i18n.t("messagePreview.noMessages")}
                  </Typography>
                </Box>
                <Box className={classes.ticketTime}>
                  <Typography style={{ fontSize: "0.75rem", color: "#fff" }}>
                    {ticket.status === "pending"
                      ? i18n.t("attendanceMonitor.tabs.waiting")
                      : i18n.t("attendanceMonitor.tabs.inService")}
                  </Typography>
                  <Typography style={{ fontSize: "0.75rem", color: "#fff", fontWeight: 700 }}>
                    {formatTime(getDisplayWaitTime(ticket))}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      {showQueueSection && !isPendingMode && (
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("attendanceMonitor.sections.queues")}
        </Typography>
      )}

      {showQueueSection && (
        <Box className={classes.attendantsGrid}>
          {filteredQueues.map((group) => (
            <Paper key={`queue-${group.id}`} className={classes.attendantCard}>
              {/* Cabeçalho do Atendente */}
              <Box className={classes.attendantHeader}>
                <Avatar
                  className={classes.attendantAvatar}
                  style={group.color ? { backgroundColor: group.color } : undefined}
                >
                  <QueueIcon />
                </Avatar>
                <Box className={classes.attendantInfo}>
                  <Typography className={classes.attendantName}>
                    {group.name}
                  </Typography>
                  <Box className={classes.statusBadge}>
                    <FiberManualRecordIcon style={{ fontSize: 10 }} />
                    {i18n.t("attendanceMonitor.queueBadge")}
                  </Box>
                </Box>
              </Box>

              {/* Lista de Tickets */}
              <Box className={classes.ticketsList}>
                {group.tickets.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <WhatsAppIcon style={{ fontSize: 40, opacity: 0.3 }} />
                    <Typography>
                      {i18n.t("attendanceMonitor.emptyQueue")}
                    </Typography>
                  </Box>
                ) : (
                  group.tickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      className={`${classes.ticketItem} ${alarmEnabled && ticket.isInAlarm ? classes.ticketItemAlarm : ""}`}
                      onClick={() => handleOpenMessages(ticket)}
                    >
                      <Avatar
                        className={classes.ticketAvatar}
                        src={ticket.contactProfilePicUrl}
                      >
                        {ticket.contactName?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Box className={classes.ticketInfo}>
                        <Typography className={classes.ticketContact}>
                          {ticket.contactName}
                          {ticket.contactNumber && (
                            <Typography
                              component="span"
                              style={{ fontSize: "0.75rem", color: grey[500], marginLeft: 8 }}
                            >
                              ({ticket.contactNumber})
                            </Typography>
                          )}
                        </Typography>
                        <Typography className={classes.ticketMessage} translate="no">
                          {ticket.lastMessageFromMe && (
                            <WhatsAppIcon style={{ fontSize: 14, color: green[500] }} />
                          )}
                          {translateMessageMarker(ticket.lastMessage) || i18n.t("messagePreview.noMessages")}
                        </Typography>
                      </Box>
                      <Box className={classes.ticketTime}>
                        <Typography style={{ fontSize: "0.75rem" }}>
                          {formatTimeShort(ticket.lastMessageAt)}
                        </Typography>
                        {getDisplayWaitTime(ticket) > 0 && (
                          <Tooltip title={i18n.t("attendanceMonitor.waitingTooltip")}>
                            <Typography
                              className={getWaitingTimeClass(
                                getDisplayWaitTime(ticket),
                                classes,
                                warningThresholdSeconds
                              )}
                              style={{ fontSize: "0.7rem" }}
                            >
                              <AccessTimeIcon style={{ fontSize: 12, verticalAlign: "middle" }} />
                              {formatTime(getDisplayWaitTime(ticket))}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

              {/* Total de Tickets */}
              <Box className={classes.totalTickets}>
                {i18n.t("attendanceMonitor.queueTotal")} <strong>{group.totalOpenTickets}</strong>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {!isPendingMode && (
        <Typography variant="h6" className={classes.sectionTitle}>
          {i18n.t("attendanceMonitor.sections.attendants")}
        </Typography>
      )}

      {!isPendingMode && (
        <Box className={classes.attendantsGrid}>
          {filteredAttendants.map((group) => (
            <Paper key={`attendant-${group.id}`} className={classes.attendantCard}>
              {/* Cabeçalho do Atendente */}
              <Box className={classes.attendantHeader}>
                <Avatar className={classes.attendantAvatar}>
                  <PersonIcon />
                </Avatar>
                <Box className={classes.attendantInfo}>
                  <Typography className={classes.attendantName}>
                    {group.name}
                  </Typography>
                  <Box className={`${classes.statusBadge} ${group.online ? classes.online : classes.offline}`}>
                    <FiberManualRecordIcon style={{ fontSize: 10 }} />
                    {group.online ? i18n.t("attendanceMonitor.status.online") : i18n.t("attendanceMonitor.status.offline")}
                  </Box>
                </Box>
              </Box>

              {/* Lista de Tickets */}
              <Box className={classes.ticketsList}>
                {group.tickets.length === 0 ? (
                  <Box className={classes.emptyState}>
                    <WhatsAppIcon style={{ fontSize: 40, opacity: 0.3 }} />
                    <Typography>
                      {i18n.t("attendanceMonitor.emptyAttendant")}
                    </Typography>
                  </Box>
                ) : (
                  group.tickets.map((ticket) => (
                    <Box
                      key={ticket.id}
                      className={`${classes.ticketItem} ${alarmEnabled && ticket.isInAlarm ? classes.ticketItemAlarm : ""}`}
                      onClick={() => handleOpenMessages(ticket)}
                    >
                      <Avatar
                        className={classes.ticketAvatar}
                        src={ticket.contactProfilePicUrl}
                      >
                        {ticket.contactName?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Box className={classes.ticketInfo}>
                        <Typography className={classes.ticketContact}>
                          {ticket.contactName}
                          {ticket.contactNumber && (
                            <Typography
                              component="span"
                              style={{ fontSize: "0.75rem", color: grey[500], marginLeft: 8 }}
                            >
                              ({ticket.contactNumber})
                            </Typography>
                          )}
                        </Typography>
                        <Typography className={classes.ticketMessage} translate="no">
                          {ticket.lastMessageFromMe && (
                            <WhatsAppIcon style={{ fontSize: 14, color: green[500] }} />
                          )}
                          {translateMessageMarker(ticket.lastMessage) || i18n.t("messagePreview.noMessages")}
                        </Typography>
                      </Box>
                      <Box className={classes.ticketTime}>
                        <Typography style={{ fontSize: "0.75rem" }}>
                          {formatTimeShort(ticket.lastMessageAt)}
                        </Typography>
                        {getDisplayWaitTime(ticket) > 0 && (
                          <Tooltip title={i18n.t("attendanceMonitor.waitingTooltip")}>
                            <Typography
                              className={getWaitingTimeClass(
                                getDisplayWaitTime(ticket),
                                classes,
                                warningThresholdSeconds
                              )}
                              style={{ fontSize: "0.7rem" }}
                            >
                              <AccessTimeIcon style={{ fontSize: 12, verticalAlign: "middle" }} />
                              {formatTime(getDisplayWaitTime(ticket))}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>

              {/* Total de Tickets */}
              <Box className={classes.totalTickets}>
                {i18n.t("attendanceMonitor.attendantTotal")} <strong>{group.totalOpenTickets}</strong>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Empty State Geral */}
      {!loading && showEmptyState && (
        <Box className={classes.emptyState}>
          <PersonIcon style={{ fontSize: 60, opacity: 0.3 }} />
          <Typography variant="h6">
            {isPendingMode ? i18n.t("attendanceMonitor.noQueues") : i18n.t("attendanceMonitor.noAttendants")}
          </Typography>
        </Box>
      )}

      {/* Modal de Chat do Ticket */}
      <TicketModal
        open={ticketModalOpen}
        onClose={handleCloseTicketModal}
        ticketId={ticketUuid}
      />
    </div>
  );
};

export default AttendanceMonitor;
